import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { listExamples, removeExample } from '../../../firebase/examples';
import { getIsAdmin } from '../selectors/users';
import { cloneProject } from '../actions/project';
import { Loader } from '../../App/components/Loader';

/**
 * "AR Examples" — a curated, world-readable list of admin-published example sketches. Reads the
 * `examples` collection and resolves each referenced project (firebase/examples.listExamples). Anyone
 * can Open (read-only unless admin) or Copy an example into their own sketches; only admins see the
 * Remove-from-examples action. Local state + a direct firebase read, mirroring SharedList.
 */
const ExamplesList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.user.authenticated);
  const isAdmin = useSelector(getIsAdmin);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listExamples()
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCopy = (row) => {
    // Metadata-only row → cloneProject fetches the source under row.ownerUsername (an example is
    // owned by an admin), copies assets, and navigates to the new owned copy.
    dispatch(cloneProject({ id: row.id, ownerUsername: row.ownerUsername }));
  };

  const handleRemove = async (row) => {
    setItems((prev) => prev.filter((it) => it.id !== row.id));
    try {
      await removeExample(row.id);
    } catch (e) {
      // best-effort; it reappears on next load if the delete failed
    }
  };

  return (
    <article className="sketches-table-container">
      <Helmet>
        <title>
          {t('ExamplesList.Title', { defaultValue: 'AR Examples' })}
        </title>
      </Helmet>
      {loading && <Loader />}
      {!loading && items.length === 0 && (
        <p className="sketches-table__empty">
          {t('ExamplesList.Empty', {
            defaultValue: 'No AR examples have been published yet.'
          })}
        </p>
      )}
      {!loading && items.length > 0 && (
        <table className="sketches-table" summary="AR examples">
          <thead>
            <tr>
              <th>{t('ExamplesList.HeaderName', { defaultValue: 'Name' })}</th>
              <th>
                {t('ExamplesList.HeaderAuthor', { defaultValue: 'Author' })}
              </th>
              <th aria-label="actions" scope="col" />
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr className="sketches-table__row" key={row.id}>
                <th scope="row">
                  <Link to={`/${row.ownerUsername}/sketches/${row.id}`}>
                    {row.name}
                  </Link>
                </th>
                <td>{row.ownerUsername}</td>
                <td>
                  {isAuthenticated && (
                    <button
                      type="button"
                      className="sketch-list__dropdown-button"
                      onClick={() => handleCopy(row)}
                    >
                      {t('ExamplesList.Copy', {
                        defaultValue: 'Copy to my sketches'
                      })}
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      className="sketch-list__dropdown-button"
                      onClick={() => handleRemove(row)}
                    >
                      {t('ExamplesList.Remove', {
                        defaultValue: 'Remove from examples'
                      })}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </article>
  );
};

export default ExamplesList;
